import { cn } from "cn";
import {
  type ComponentProps,
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import WaveSurfer from "wavesurfer.js";
import { Button } from "@/components/ui/button";
import {
  BwDownloadLine,
  BwMoreFill,
  BwPauseLine,
  BwPlayFill,
} from "@/components/ui/icons";
import { getDownloadUrl } from "@/components/ui/lightbox/utils";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { Separator } from "@/components/ui/separator";
import SliderWithTicks from "@/components/ui/slider/slider-with-ticks";

type AudioRecordPlayerContextValue = ReturnType<typeof useAudioRecordPlayer> & {
  recordedUrl: string | null | undefined;
  onEnded?: () => void;
};

const AudioRecordPlayerContext =
  createContext<AudioRecordPlayerContextValue | null>(null);

export function useAudioRecordPlayerContext() {
  const ctx = useContext(AudioRecordPlayerContext);
  if (!ctx) {
    throw new Error(
      "AudioRecordPlayer.* must be used within <AudioRecordPlayer>",
    );
  }
  return ctx;
}

type AudioRecordPlayerProps = ComponentProps<"div"> & {
  recordedUrl: string | null | undefined;
  autoPlay?: boolean;
  onEnded?: () => void;
};

function cssVar(name: string, el: HTMLElement = document.documentElement) {
  return getComputedStyle(el).getPropertyValue(name).trim();
}

function formatSecondsToClock(
  totalSeconds: number,
  mode: "mm:ss" | "ss" = "mm:ss",
) {
  const clamped = Math.max(0, Math.floor(totalSeconds ?? 0));
  if (mode === "ss") return String(clamped);
  const minutes = Math.floor(clamped / 60);
  const seconds = clamped % 60;
  return `${String(minutes).padStart(2, "0")}:${String(seconds).padStart(2, "0")}`;
}

function useAudioRecordPlayer(params: {
  recordedUrl: string | null | undefined;
  autoPlay?: boolean;
  onEnded?: () => void;
}) {
  const { recordedUrl, autoPlay = false, onEnded } = params;

  const containerRef = useRef<HTMLDivElement | null>(null);
  const wavesurferRef = useRef<WaveSurfer | null>(null);
  const [isReady, setIsReady] = useState(false);
  const [isPlaying, setIsPlaying] = useState(false);
  const [duration, setDuration] = useState(0);
  const [currentTime, setCurrentTime] = useState(0);
  const [playbackRate, setPlaybackRate] = useState(1);

  useEffect(() => {
    if (!containerRef.current) return;

    wavesurferRef.current?.destroy();
    wavesurferRef.current = null;
    setIsReady(false);
    setIsPlaying(false);
    setDuration(0);
    setCurrentTime(0);

    const waveColor = "#D6D6D6";
    const progressColor = cssVar("--color-text-primary");

    const ws = WaveSurfer.create({
      container: containerRef.current,
      // MediaElement (default) uses <audio> — Safari/iOS decode WebM/Web Audio poorly; MP3 + HTML audio is reliable.
      backend: "MediaElement",
      height: 32,
      barGap: 1,
      waveColor,
      progressColor,
      cursorWidth: 1,
      barWidth: 2,
      barMinHeight: 2,
      barRadius: 9999,
    });

    ws.on("ready", () => {
      setIsReady(true);
      setDuration(Math.floor(ws.getDuration() || 0));
      ws.setPlaybackRate(playbackRate);
      if (autoPlay) {
        ws.play();
      }
    });

    ws.on("timeupdate", (time: number) => {
      if (!time) return;
      setCurrentTime(Math.floor(time || 0));
    });

    ws.on("play", () => setIsPlaying(true));
    ws.on("pause", () => setIsPlaying(false));
    ws.on("finish", () => {
      setIsPlaying(false);
      setCurrentTime(Math.floor(ws.getDuration() || 0));
      onEnded?.();
    });

    wavesurferRef.current = ws;

    return () => {
      ws.destroy();
    };
  }, [autoPlay, onEnded]);

  useEffect(() => {
    const ws = wavesurferRef.current;
    if (!ws || !recordedUrl) return;
    setIsReady(false);
    setIsPlaying(false);
    setDuration(0);
    setCurrentTime(0);
    // wavesurfer 7 aborts an in-flight load when the url changes or the instance is
    // destroyed (a memo's original swapping for its rendition, StrictMode's remount) and
    // rejects that load's promise with an AbortError — expected, not a failure, and left
    // uncaught it surfaces as "Uncaught (in promise) AbortError: signal is aborted without
    // reason" in every console. Anything else is still worth seeing.
    ws.load(recordedUrl).catch((error: unknown) => {
      if ((error as { name?: string } | null)?.name === "AbortError") return;
      console.error("audio player load failed", error);
    });
  }, [recordedUrl]);

  useEffect(() => {
    const ws = wavesurferRef.current;
    if (!ws || !isReady) return;
    ws.setPlaybackRate(playbackRate);
  }, [playbackRate, isReady]);

  const play = useCallback(() => {
    const ws = wavesurferRef.current;
    if (!ws || !isReady) return;
    ws.play();
  }, [isReady]);

  const pause = useCallback(() => {
    const ws = wavesurferRef.current;
    if (!ws || !isReady) return;
    ws.pause();
  }, [isReady]);

  const stop = useCallback(() => {
    const ws = wavesurferRef.current;
    if (!ws) return;
    // stop() stops playback and resets to the beginning
    ws.stop();
    setIsPlaying(false);
    setCurrentTime(0);
  }, []);

  const togglePlay = useCallback(() => {
    const ws = wavesurferRef.current;
    if (!ws || !isReady) return;
    if (ws.isPlaying()) {
      ws.pause();
    } else {
      ws.play();
    }
  }, [isReady]);

  const seekTo = useCallback(
    (progress: number) => {
      const ws = wavesurferRef.current;
      if (!ws || !isReady) return;
      const clamped = Math.max(0, Math.min(1, progress));
      ws.seekTo(clamped);
    },
    [isReady],
  );

  return {
    containerRef,
    isReady,
    isPlaying,
    duration,
    currentTime,
    play,
    pause,
    stop,
    togglePlay,
    seekTo,
    playbackRate,
    setPlaybackRate,
  };
}

function AudioRecordPlayer({
  className,
  recordedUrl,
  autoPlay = false,
  onEnded,
  children,
  ...props
}: AudioRecordPlayerProps) {
  const player = useAudioRecordPlayer({ recordedUrl, autoPlay, onEnded });

  useEffect(() => {
    return () => {
      player.stop();
    };
  }, []);

  if (!recordedUrl) return null;

  return (
    <AudioRecordPlayerContext.Provider
      value={{ ...player, recordedUrl, onEnded }}
    >
      <div
        data-slot="audio-record-player"
        className={cn(
          /* sorted */
          "relative overflow-hidden flex",
          "gap-2 items-center",
          "w-full",
          "p-2",
          "bg-bg-dark-min rounded-lg border border-border-dark-min",
          className,
        )}
        {...props}
      >
        {children}
      </div>
    </AudioRecordPlayerContext.Provider>
  );
}

type AudioRecordPlayerWaveProps = ComponentProps<"div">;

function AudioRecordPlayerWave({
  className,
  ...props
}: AudioRecordPlayerWaveProps) {
  const { containerRef } = useAudioRecordPlayerContext();
  return (
    <div
      data-slot="audio-record-player-wave"
      ref={containerRef}
      className={cn(
        /* sorted */
        "relative overflow-hidden",
        "w-full",
        "waveform cursor-pointer",
        className,
      )}
      {...props}
    />
  );
}

type AudioRecordPlayerDurationProps = ComponentProps<"span"> & {
  variant?: "current" | "total" | "range";
  format?: "mm:ss" | "ss";
};

function AudioRecordPlayerDuration({
  className,
  format = "mm:ss",
  ...props
}: Omit<AudioRecordPlayerDurationProps, "variant">) {
  const { currentTime, duration, isPlaying } = useAudioRecordPlayerContext();
  const [hasPlayed, setHasPlayed] = useState(false);

  // Track if playback has ever started
  useEffect(() => {
    if (isPlaying && !hasPlayed) {
      setHasPlayed(true);
    }
  }, [isPlaying, hasPlayed]);

  const text = useMemo(() => {
    // Before ever playing, show total duration
    // After any playback, show current time (keep frozen when paused)
    if (hasPlayed) {
      return formatSecondsToClock(currentTime, format);
    }
    return formatSecondsToClock(duration, format);
  }, [currentTime, duration, hasPlayed, format]);

  return (
    <span
      data-slot="audio-record-player-duration"
      className={cn(
        /* sorted */
        "shrink-0",
        "min-w-9",
        "text-xs text-text-dark-low text-right",
        className,
      )}
      {...props}
    >
      {text}
    </span>
  );
}

type AudioRecordPlayerTogglePlayProps = ComponentProps<typeof Button>;

function AudioRecordPlayerTogglePlay({
  className,
  children,
  ...props
}: AudioRecordPlayerTogglePlayProps) {
  const { isReady, isPlaying, togglePlay } = useAudioRecordPlayerContext();
  const handleClick = useCallback(() => {
    togglePlay();
  }, [togglePlay]);
  const content = useMemo(() => {
    if (children) return children;
    return !isPlaying ? <BwPlayFill /> : <BwPauseLine />;
  }, [children, isPlaying]);
  return (
    <Button
      data-slot="audio-record-player-toggle-play"
      size="icon-md"
      onClick={handleClick}
      disabled={!isReady}
      className={cn("group", className)}
      {...props}
    >
      {content}
    </Button>
  );
}

type AudioRecordPlayerOptionProps = ComponentProps<typeof Button>;

function AudioRecordPlayerOption({
  className,
  ...props
}: AudioRecordPlayerOptionProps) {
  const { recordedUrl, playbackRate, setPlaybackRate } =
    useAudioRecordPlayerContext();
  const rates = [0.25, 0.5, 0.75, 1, 1.25, 1.5, 2];
  const currentIndex = useMemo(() => {
    const idx = rates.findIndex((r) => r === playbackRate);
    return idx >= 0 ? idx : (rates.findIndex((r) => r === 1) ?? 3);
  }, [rates, playbackRate]);
  const handleSliderChange = useCallback(
    (values: number[]) => {
      const idx = Math.round(values[0] ?? 3);
      const rate = rates[Math.max(0, Math.min(rates.length - 1, idx))];
      setPlaybackRate(rate);
    },
    [rates, setPlaybackRate],
  );

  return (
    <Popover>
      <PopoverTrigger asChild>
        <Button
          size="icon-md"
          variant="neutral"
          kind="ghost"
          className={cn(className)}
          {...props}
        >
          <BwMoreFill size={20} />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="p-1 w-72 rounded-lg" align="end">
        <p className="text-sm text-text-dark-low py-1.5 px-2">Playback speed</p>
        <div className="p-2">
          <SliderWithTicks
            skipInterval={1}
            min={0}
            max={rates.length - 1}
            step={1}
            value={[currentIndex]}
            customTicks={rates}
            onValueChange={handleSliderChange}
            className="w-full **:data-[slot=slider-track]:h-2"
          />
        </div>
        <Separator className="-mx-1 data-[orientation=horizontal]:w-auto" />
        <a
          className="p-2 items-center flex gap-2 cursor-pointer mt-1"
          href={getDownloadUrl(recordedUrl as string)}
          download={true}
          target="_self"
          rel="noreferrer"
        >
          <BwDownloadLine className="text-text-dark-high size-5 shrink-0" />
          <p className="text-sm grow">Download</p>
        </a>
      </PopoverContent>
    </Popover>
  );
}

export {
  AudioRecordPlayer,
  AudioRecordPlayerDuration,
  AudioRecordPlayerOption,
  AudioRecordPlayerTogglePlay,
  AudioRecordPlayerWave,
};
